import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, FileText } from 'lucide-react';
type Status = 'Completed' | 'No Content' | 'Workers Signing';
interface Record {
  id: string;
  fileType: 'pdf' | 'missing';
  fileName: string;
  site: string;
  subName: string;
  date: string;
  status: Status;
}
const SITES = ['1760 3rd Ave', '1760 Jerome Ave', 'Court Square LIC', '99 Claremont Ave', '251 Douglass Street', '450 Park Avenue South', '520 Fifth Avenue'];
const SUBS = ['Aurora Paint Corp.', 'Skytrac', 'Flushing Iron Weld', 'Sector Security', 'Luxintec', 'V10 Construction', 'Kitchens Of USA', 'Otis Elevator Company', 'Sunkeeper Solar', 'Architectural Flooring Resource Inc'];
function generateMockData(): Record[] {
  const data: Record[] = [];
  let id = 1;
  const add = (date: string, status: Status, subIdx: number, siteIdx: number) => {
    const isCompleted = status === 'Completed';
    data.push({
      id: String(id++),
      fileType: isCompleted ? 'pdf' : status === 'Workers Signing' ? 'pdf' : 'missing',
      fileName: isCompleted ? 'Signed JHA' : '',
      site: SITES[siteIdx % SITES.length],
      subName: SUBS[subIdx % SUBS.length],
      date,
      status
    });
  };
  // Entire Project
  for (let i = 0; i < 25; i++) add('03/14/2026', 'No Content', i, i);
  for (let i = 0; i < 15; i++) add('03/12/2026', 'Workers Signing', i + 2, i + 1);
  for (let i = 0; i < 40; i++) add('03/10/2026', 'Completed', i + 4, i + 2);
  return data;
}
const MOCK_DATA = generateMockData();
export function JHAPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [periodLabel, setPeriodLabel] = useState<string | null>(null);
  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  useEffect(() => {
    const period = searchParams.get('period');
    const status = searchParams.get('status');
    const site = searchParams.get('site');
    if (status === 'incomplete') {
      setStatusFilter('Incomplete');
    } else {
      setStatusFilter(null);
    }
    if (site) {
      setSiteFilter(site);
    } else {
      setSiteFilter(null);
    }
    if (period === 'entireproject') {
      setPeriodLabel('Entire project(s)');
    } else {
      setPeriodLabel(null);
    }
  }, [searchParams]);
  const clearAllFilters = () => {
    setStatusFilter(null);
    setPeriodLabel(null);
    setSiteFilter(null);
    setSearchParams({});
  };
  const removeFilter = (key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams);
  };
  const filteredData = useMemo(() => {
    return MOCK_DATA.filter(item => {
      if (searchQuery && !item.subName.toLowerCase().includes(searchQuery.toLowerCase()) && !item.site.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (statusFilter === 'Incomplete') {
        if (item.status === 'Completed') return false;
      } else if (statusFilter && item.status !== statusFilter) {
        return false;
      }
      if (siteFilter && item.site !== siteFilter) {
        return false;
      }
      return true;
    });
  }, [searchQuery, statusFilter, siteFilter]);
  const hasActiveFilters = statusFilter !== null || periodLabel !== null || siteFilter !== null;
  return <div className="min-h-screen bg-[#fafafa] font-sans flex flex-col" data-id="element-4271" data-mp="subpa118p4bo">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4" data-id="element-4272" data-mp="xnobk4ig4z19">
        <div className="flex items-center gap-2" data-id="element-4273" data-mp="11ngcpw5ig2wl">
          <div className="text-blue-600 font-bold text-2xl tracking-tighter flex items-center" data-id="element-4274" data-mp="16nuh947wkupv">
            CHST
            <div className="w-6 h-6 border-4 border-blue-600 rounded-full mx-0.5 relative" data-id="element-4275" data-mp="1bme91l96h0q5">
              <div className="absolute inset-0 m-auto w-2 h-2 bg-blue-600 rounded-full" data-id="element-4276" data-mp="xzx7zfjaje5i"></div>
            </div>
          </div>
          <div className="text-[10px] font-bold text-blue-600 tracking-widest absolute mt-8 ml-1" data-id="element-4277" data-mp="t02m5qedherb">
            L D E R S
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 ml-4" data-id="element-4278" data-mp="17run2xdf6q9i">JHA</h1>
      </div>

      <main className="flex-1 p-6 max-w-[1600px] mx-auto w-full" data-id="element-4279" data-mp="12rp0hvm0btya">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-4280" data-mp="f2dxs8l3jphi">
          <h2 className="text-xl text-gray-700" data-id="element-4281" data-mp="a3b39i2qt1ns">
            Found{' '}
            <span className="font-semibold text-gray-900" data-id="element-4282" data-mp="oz8t6im2p9rk">
              {filteredData.length}
            </span>{' '}
            JHAs
          </h2>

          <div className="flex items-center gap-3" data-id="element-4283" data-mp="jz4duvwwil2x">
            <div className="relative" data-id="element-4284" data-mp="20e5xt0uqq7k1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-4285" data-mp="20d4c82g3m2o2" />
              <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4286" data-mp="22zderijxkuky" />
            </div>
            <button className="relative flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 bg-white transition-colors shadow-sm" data-id="element-4287" data-mp="2lplc1k0y8qt8">
              <SlidersHorizontal size={16} data-id="element-4288" data-mp="2cx9oc5x7zhgy" />
              Filters
              {hasActiveFilters && <span className="absolute top-0 right-0 -mt-1 -mr-1 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" data-id="element-4289" data-mp="2gyv4q18yqdvw"></span>}
            </button>
          </div>
        </div>

        {hasActiveFilters && <div className="flex items-center flex-wrap gap-2 mb-6" data-id="element-4290" data-mp="39g7jb934q726">
            {siteFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4291" data-mp="3ja4t4oaeantm">
                <span className="text-gray-300" data-id="element-4292" data-mp="3fa2oq7d9zgan">Site:</span>
                <span className="font-medium" data-id="element-4293" data-mp="3a9yhl7y9bxx3">{siteFilter}</span>
                <button onClick={() => removeFilter('site')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4294" data-mp="3ackm0ypupxb3">
                  <X size={14} data-id="element-4295" data-mp="30dle5icwu5p5" />
                </button>
              </div>}

            {statusFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4296" data-mp="2vjqv8h54pur4">
                <span className="text-gray-300" data-id="element-4297" data-mp="2ml5i51iff7xq">Status:</span>
                <span className="font-medium" data-id="element-4298" data-mp="2mmqbwowx2muj">{statusFilter}</span>
                <button onClick={() => removeFilter('status')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4299" data-mp="2hkr7rctcrlwj">
                  <X size={14} data-id="element-4300" data-mp="22jaokgaerg5b" />
                </button>
              </div>}

            {periodLabel && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4301" data-mp="27gkleh04mu36">
                <span className="text-gray-300" data-id="element-4302" data-mp="2chi18mxnzc1i">Period:</span>
                <span className="font-medium" data-id="element-4303" data-mp="2hgl5441wrc7w">{periodLabel}</span>
                <button onClick={() => removeFilter('period')} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4304" data-mp="2hdp9imdj8zmm">
                  <X size={14} data-id="element-4305" data-mp="1op9wu6hviteg" />
                </button>
              </div>}

            <button onClick={clearAllFilters} className="text-sm text-gray-500 hover:text-gray-900 underline underline-offset-2 ml-2 transition-colors" data-id="element-4306" data-mp="1tp6186uhgmuj">
              Clear all
            </button>
          </div>}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4307" data-mp="1603a88dylhnd">
          <div className="overflow-x-auto" data-id="element-4308" data-mp="2k6l4qxqvqjq4">
            <table className="w-full text-left text-sm" data-id="element-4309" data-mp="2k9fnmku8vwhf">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-semibold text-xs tracking-wider" data-id="element-4310" data-mp="2g6j8j43wyii8">
                <tr data-id="element-4311" data-mp="2u73weeoor4il">
                  <th className="px-6 py-4" data-id="element-4312" data-mp="2u3g1pc7i8c2a">FILE</th>
                  <th className="px-6 py-4" data-id="element-4313" data-mp="2p66bhucajwde">SITE</th>
                  <th className="px-6 py-4" data-id="element-4314" data-mp="2p8sm62uharzu">SUB NAME</th>
                  <th className="px-6 py-4" data-id="element-4315" data-mp="334wchl28eg71">DATE</th>
                  <th className="px-6 py-4" data-id="element-4316" data-mp="2z4ltn7tcbvar">STATUS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-4317" data-mp="3cw0pwgc9dszr">
                {filteredData.map(row => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-4318" data-mp="37x7bmlp5dxyj">
                    <td className="px-6 py-4" data-id="element-4319" data-mp="3muzr5dup2mcq">
                      <div className="flex items-center gap-3" data-id="element-4320" data-mp="3hv3lzba9govl">
                        {row.fileType === 'pdf' ? <div className="relative" data-id="element-4321" data-mp="5azjw85t50wx">
                            <FileText size={24} className="text-gray-300" data-id="element-4322" data-mp="5cap8cewigq5" />
                            <div className="absolute bottom-0 left-0 bg-red-600 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4323" data-mp="9bt8l3r2qjqv">
                              PDF
                            </div>
                          </div> : <div className="relative" data-id="element-4324" data-mp="3ht62n1fofs60">
                            <FileText size={24} className="text-gray-200" data-id="element-4325" data-mp="3hrmnje5rbn5t" />
                            <div className="absolute bottom-0 left-0 bg-gray-500 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4326" data-mp="3mko37odsfqqg">
                              ?
                            </div>
                          </div>}
                        <span className="text-gray-900" data-id="element-4327" data-mp="2yu1xw4v64kk5">{row.fileName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4328" data-mp="33tej6l6e5jkm">{row.site}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4329" data-mp="13hoe9zedlycs">{row.subName}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4330" data-mp="13jrflfkzbdwt">{row.date}</td>
                    <td className="px-6 py-4" data-id="element-4331" data-mp="18icenbohvrjo">
                      {row.status === 'Completed' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-4332" data-mp="tl1uzf1p7fcv">
                          Completed
                        </span>}
                      {row.status === 'No Content' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700" data-id="element-4333" data-mp="1r5xwkrcds3z6">
                          No Content
                        </span>}
                      {row.status === 'Workers Signing' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-4334" data-mp="255z6sxejcnrb">
                          Workers Signing
                        </span>}
                    </td>
                  </tr>)}
                {filteredData.length === 0 && <tr data-id="element-4335" data-mp="3pn8dqi7a3l9p">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-4336" data-mp="88bbssqb5iaz">
                      No JHAs found matching your criteria.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>;
}